import React, { useRef, useEffect, useCallback } from 'react';
import {
  ISO_CONFIG,
  IsoSimulationState,
  IsoAgent,
  IsoKPIDisplay,
  DayLighting,
  isoToScreen,
  calculateDepth,
} from '../../../types/isometric.types';

interface IsometricCanvasProps {
  state: IsoSimulationState;
  onAgentClick?: (agent: IsoAgent) => void;
  onKPIClick?: (kpi: IsoKPIDisplay) => void;
  className?: string;
}

// Color palette for modern corporate office
const COLORS = {
  floor: {
    main: '#E8E4D9',
    pattern: '#DDD9CE',
    shadow: 'rgba(0,0,0,0.05)',
  },
  carpet: {
    main: '#4A6FA5',
    pattern: '#3D5C8A',
  },
  wall: {
    main: '#F5F5F5',
    accent: '#E0E0E0',
    glass: 'rgba(200, 220, 255, 0.4)',
  },
  furniture: {
    desk: '#8B7355',
    deskTop: '#A08060',
    chair: '#2D3748',
    chairSeat: '#4299E1',
    plant: '#48BB78',
    plantPot: '#A0522D',
  },
  kpiScreen: {
    background: '#1A202C',
    border: '#4A5568',
    excellent: '#48BB78',
    good: '#4299E1',
    warning: '#ECC94B',
    critical: '#F56565',
  },
};

export const IsometricCanvas: React.FC<IsometricCanvasProps> = ({
  state,
  onAgentClick: _onAgentClick,
  onKPIClick: _onKPIClick,
  className,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const lastTimeRef = useRef<number>(0);

  const { CANVAS_WIDTH, CANVAS_HEIGHT, TILE_WIDTH, TILE_HEIGHT, GRID_COLS, GRID_ROWS } = ISO_CONFIG;

  // Calculate canvas origin (center-top for isometric view)
  const originX = CANVAS_WIDTH / 2;
  const originY = 100;

  // Draw an isometric diamond tile
  const drawTile = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      col: number,
      row: number,
      color: string,
      patternColor?: string
    ) => {
      const { x, y } = isoToScreen(col, row, originX, originY);

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x + TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
      ctx.lineTo(x, y + TILE_HEIGHT);
      ctx.lineTo(x - TILE_WIDTH / 2, y + TILE_HEIGHT / 2);
      ctx.closePath();

      ctx.fillStyle = color;
      ctx.fill();

      // Add subtle pattern
      if (patternColor && (col + row) % 2 === 0) {
        ctx.fillStyle = patternColor;
        ctx.fill();
      }

      // Subtle border
      ctx.strokeStyle = 'rgba(0,0,0,0.05)';
      ctx.lineWidth = 0.5;
      ctx.stroke();
    },
    [originX, originY, TILE_WIDTH, TILE_HEIGHT]
  );

  // Draw a 3D isometric box (for furniture/objects)
  const drawIsoBox = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      col: number,
      row: number,
      width: number,
      depth: number,
      height: number,
      topColor: string,
      leftColor: string,
      rightColor: string
    ) => {
      const { x, y } = isoToScreen(col, row, originX, originY);

      // Calculate box vertices
      const hw = (width * TILE_WIDTH) / 2;
      const hd = (depth * TILE_HEIGHT) / 2;

      // Top face
      ctx.beginPath();
      ctx.moveTo(x, y - height);
      ctx.lineTo(x + hw, y + hd / 2 - height);
      ctx.lineTo(x, y + hd - height);
      ctx.lineTo(x - hw, y + hd / 2 - height);
      ctx.closePath();
      ctx.fillStyle = topColor;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.1)';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Left face
      ctx.beginPath();
      ctx.moveTo(x - hw, y + hd / 2 - height);
      ctx.lineTo(x, y + hd - height);
      ctx.lineTo(x, y + hd);
      ctx.lineTo(x - hw, y + hd / 2);
      ctx.closePath();
      ctx.fillStyle = leftColor;
      ctx.fill();
      ctx.stroke();

      // Right face
      ctx.beginPath();
      ctx.moveTo(x + hw, y + hd / 2 - height);
      ctx.lineTo(x, y + hd - height);
      ctx.lineTo(x, y + hd);
      ctx.lineTo(x + hw, y + hd / 2);
      ctx.closePath();
      ctx.fillStyle = rightColor;
      ctx.fill();
      ctx.stroke();
    },
    [originX, originY, TILE_WIDTH, TILE_HEIGHT]
  );

  // Draw modern desk
  const drawDesk = useCallback(
    (ctx: CanvasRenderingContext2D, col: number, row: number) => {
      // Desk surface
      drawIsoBox(ctx, col, row, 1.5, 1, 20, '#D4C4B0', '#A08060', '#8B7355');

      // Monitor
      const { x, y } = isoToScreen(col, row, originX, originY);
      // Monitor back
      ctx.fillStyle = '#2D3748';
      ctx.fillRect(x - 12, y - 50, 24, 20);
      // Monitor screen
      ctx.fillStyle = '#4299E1';
      ctx.fillRect(x - 10, y - 48, 20, 16);
      // Monitor stand
      ctx.fillStyle = '#4A5568';
      ctx.fillRect(x - 3, y - 30, 6, 10);
    },
    [drawIsoBox, originX, originY]
  );

  // Draw office chair
  const drawChair = useCallback(
    (ctx: CanvasRenderingContext2D, col: number, row: number, color: string = '#4299E1') => {
      const { x, y } = isoToScreen(col, row, originX, originY);

      // Seat cushion
      ctx.beginPath();
      ctx.ellipse(x, y - 15, 14, 8, 0, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.2)';
      ctx.stroke();

      // Back rest
      ctx.beginPath();
      ctx.ellipse(x, y - 35, 12, 15, 0, Math.PI, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.stroke();

      // Base/wheels
      ctx.fillStyle = '#2D3748';
      ctx.beginPath();
      ctx.arc(x, y, 8, 0, Math.PI * 2);
      ctx.fill();
    },
    [originX, originY]
  );

  // Draw plant
  const drawPlant = useCallback(
    (ctx: CanvasRenderingContext2D, col: number, row: number, isLarge: boolean = true) => {
      const { x, y } = isoToScreen(col, row, originX, originY);
      const scale = isLarge ? 1 : 0.6;

      // Pot
      ctx.beginPath();
      ctx.moveTo(x - 15 * scale, y - 5);
      ctx.lineTo(x + 15 * scale, y - 5);
      ctx.lineTo(x + 12 * scale, y + 20 * scale);
      ctx.lineTo(x - 12 * scale, y + 20 * scale);
      ctx.closePath();
      ctx.fillStyle = '#A0522D';
      ctx.fill();

      // Foliage (multiple circles for bush effect)
      ctx.fillStyle = '#48BB78';
      const foliagePositions = [
        { dx: 0, dy: -30 * scale, r: 18 * scale },
        { dx: -12 * scale, dy: -20 * scale, r: 12 * scale },
        { dx: 12 * scale, dy: -20 * scale, r: 12 * scale },
        { dx: -8 * scale, dy: -40 * scale, r: 10 * scale },
        { dx: 8 * scale, dy: -40 * scale, r: 10 * scale },
      ];

      foliagePositions.forEach(({ dx, dy, r }) => {
        ctx.beginPath();
        ctx.arc(x + dx, y + dy, r, 0, Math.PI * 2);
        ctx.fill();
      });
    },
    [originX, originY]
  );

  // Draw coffee machine
  const drawCoffeeMachine = useCallback(
    (ctx: CanvasRenderingContext2D, col: number, row: number) => {
      const { x, y } = isoToScreen(col, row, originX, originY);

      // Machine body
      drawIsoBox(ctx, col, row, 0.6, 0.4, 40, '#4A5568', '#2D3748', '#1A202C');

      // Water tank (glass effect)
      ctx.fillStyle = 'rgba(66, 153, 225, 0.5)';
      ctx.fillRect(x - 8, y - 55, 16, 20);

      // Drip tray
      ctx.fillStyle = '#718096';
      ctx.fillRect(x - 10, y - 10, 20, 5);

      // Coffee cup
      ctx.fillStyle = '#F7FAFC';
      ctx.beginPath();
      ctx.arc(x, y - 5, 6, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#6B4423';
      ctx.beginPath();
      ctx.arc(x, y - 5, 4, 0, Math.PI * 2);
      ctx.fill();
    },
    [originX, originY, drawIsoBox]
  );

  // Draw KPI display screen (LEFT)
  const drawKPIScreen = useCallback(
    (ctx: CanvasRenderingContext2D, col: number, row: number, kpis: IsoKPIDisplay[]) => {
      const { x, y } = isoToScreen(col, row, originX, originY);

      // Screen frame with blue accent
      ctx.fillStyle = '#1A202C';
      ctx.fillRect(x - 80, y - 140, 160, 110);

      // Screen border glow - blue
      ctx.strokeStyle = '#3B82F6';
      ctx.lineWidth = 3;
      ctx.strokeRect(x - 80, y - 140, 160, 110);

      // Screen content
      ctx.fillStyle = '#0D1117';
      ctx.fillRect(x - 75, y - 135, 150, 100);

      // KPI header
      ctx.fillStyle = '#3B82F6';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('📊 REAL-TIME KPIs', x, y - 120);

      // Draw each KPI
      kpis.slice(0, 3).forEach((kpi, i) => {
        const kpiY = y - 100 + i * 28;

        // KPI name
        ctx.fillStyle = '#A0AEC0';
        ctx.font = '9px Inter, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(kpi.name, x - 68, kpiY);

        // Progress bar background
        ctx.fillStyle = '#2D3748';
        ctx.fillRect(x - 68, kpiY + 4, 95, 10);

        // Progress bar fill
        const progress = Math.min(100, (kpi.value / kpi.target) * 100);
        const barColor =
          kpi.status === 'EXCELLENT'
            ? COLORS.kpiScreen.excellent
            : kpi.status === 'GOOD'
              ? COLORS.kpiScreen.good
              : kpi.status === 'WARNING'
                ? COLORS.kpiScreen.warning
                : COLORS.kpiScreen.critical;

        ctx.fillStyle = barColor;
        ctx.fillRect(x - 68, kpiY + 4, (progress / 100) * 95, 10);

        // Value text
        ctx.fillStyle = '#F7FAFC';
        ctx.font = 'bold 9px JetBrains Mono, monospace';
        ctx.textAlign = 'right';
        ctx.fillText(`${kpi.value}/${kpi.target}`, x + 68, kpiY + 11);
      });
    },
    [originX, originY]
  );

  // Draw Alarm display screen (CENTER)
  const drawAlarmScreen = useCallback(
    (ctx: CanvasRenderingContext2D, col: number, row: number, kpis: IsoKPIDisplay[], animFrame: number) => {
      const { x, y } = isoToScreen(col, row, originX, originY);
      const activeAlarms = kpis.filter(k => k.alarmActive);
      const hasAlarms = activeAlarms.length > 0;

      // Screen frame - red glow if alarms active
      ctx.fillStyle = '#1A202C';
      ctx.fillRect(x - 80, y - 140, 160, 110);

      // Screen border glow - pulsing red if alarms, otherwise green
      const pulse = hasAlarms ? (Math.sin(animFrame * 0.1) + 1) / 2 : 0;
      ctx.strokeStyle = hasAlarms
        ? `rgba(239, 68, 68, ${0.6 + pulse * 0.4})`
        : '#10B981';
      ctx.lineWidth = 3;
      ctx.strokeRect(x - 80, y - 140, 160, 110);

      // Screen content
      ctx.fillStyle = hasAlarms ? '#1C1917' : '#0D1117';
      ctx.fillRect(x - 75, y - 135, 150, 100);

      // Alarm header
      ctx.fillStyle = hasAlarms ? '#EF4444' : '#10B981';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(hasAlarms ? '🚨 ACTIVE ALARMS' : '✅ ALL SYSTEMS OK', x, y - 120);

      if (hasAlarms) {
        // Draw active alarms
        activeAlarms.slice(0, 3).forEach((kpi, i) => {
          const alarmY = y - 100 + i * 28;

          // Alarm background (pulsing)
          ctx.fillStyle = `rgba(239, 68, 68, ${0.2 + pulse * 0.2})`;
          ctx.fillRect(x - 70, alarmY - 5, 140, 22);

          // Warning icon
          ctx.fillStyle = '#EF4444';
          ctx.font = '12px sans-serif';
          ctx.textAlign = 'left';
          ctx.fillText('⚠️', x - 65, alarmY + 8);

          // Alarm name
          ctx.fillStyle = '#FECACA';
          ctx.font = 'bold 9px Inter, sans-serif';
          ctx.fillText(kpi.name, x - 45, alarmY + 5);

          // Alarm detail
          ctx.fillStyle = '#FCA5A5';
          ctx.font = '8px Inter, sans-serif';
          ctx.fillText(`${kpi.value}/${kpi.target} - ${kpi.status}`, x - 45, alarmY + 14);
        });
      } else {
        // No alarms - show status
        ctx.fillStyle = '#10B981';
        ctx.font = '24px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText('✓', x, y - 75);

        ctx.fillStyle = '#6EE7B7';
        ctx.font = '10px Inter, sans-serif';
        ctx.fillText('All KPIs within thresholds', x, y - 55);
        ctx.fillText('System operating normally', x, y - 42);
      }
    },
    [originX, originY]
  );

  // Draw Recommendations display screen (RIGHT)
  const drawRecommendScreen = useCallback(
    (ctx: CanvasRenderingContext2D, col: number, row: number) => {
      const { x, y } = isoToScreen(col, row, originX, originY);

      // Screen frame with purple accent
      ctx.fillStyle = '#1A202C';
      ctx.fillRect(x - 80, y - 140, 160, 110);

      // Screen border glow - purple
      ctx.strokeStyle = '#8B5CF6';
      ctx.lineWidth = 3;
      ctx.strokeRect(x - 80, y - 140, 160, 110);

      // Screen content
      ctx.fillStyle = '#0D1117';
      ctx.fillRect(x - 75, y - 135, 150, 100);

      // Header
      ctx.fillStyle = '#8B5CF6';
      ctx.font = 'bold 11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('💡 RECOMMENDATIONS', x, y - 120);

      // Sample recommendations
      const recommendations = [
        { icon: '🎯', text: 'Apply to 3 more jobs today' },
        { icon: '📚', text: 'Complete React module' },
        { icon: '🤝', text: 'Connect with 2 recruiters' },
      ];

      recommendations.forEach((rec, i) => {
        const recY = y - 98 + i * 26;

        // Recommendation background
        ctx.fillStyle = 'rgba(139, 92, 246, 0.1)';
        ctx.fillRect(x - 70, recY - 3, 140, 20);

        // Icon
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(rec.icon, x - 65, recY + 10);

        // Text
        ctx.fillStyle = '#C4B5FD';
        ctx.font = '9px Inter, sans-serif';
        ctx.fillText(rec.text, x - 48, recY + 10);
      });

      // Priority badge
      ctx.fillStyle = '#8B5CF6';
      ctx.beginPath();
      ctx.arc(x + 60, y - 118, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#FFF';
      ctx.font = 'bold 8px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('3', x + 60, y - 115);
    },
    [originX, originY]
  );

  // Draw agent - ENHANCED for better visibility
  const drawAgent = useCallback(
    (ctx: CanvasRenderingContext2D, agent: IsoAgent, animFrame: number) => {
      const { x, y } = isoToScreen(agent.position.x, agent.position.y, originX, originY);

      // Animation offset for walking
      const bobOffset = agent.activity === 'WALKING' ? Math.sin(animFrame * 0.3) * 3 : 0;
      const legOffset = agent.activity === 'WALKING' ? Math.sin(animFrame * 0.6) * 4 : 0;

      // Shadow (larger, more visible)
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.beginPath();
      ctx.ellipse(x, y + 8, 18, 8, 0, 0, Math.PI * 2);
      ctx.fill();

      // Outer glow effect for visibility
      ctx.beginPath();
      ctx.arc(x, y - 30 + bobOffset, 35, 0, Math.PI * 2);
      ctx.fillStyle = agent.style.glowColor;
      ctx.fill();

      // Legs (animated when walking)
      ctx.fillStyle = '#374151';
      ctx.fillRect(x - 8 - legOffset, y - 15 + bobOffset, 6, 18);
      ctx.fillRect(x + 2 + legOffset, y - 15 + bobOffset, 6, 18);

      // Shoes
      ctx.fillStyle = '#1F2937';
      ctx.beginPath();
      ctx.ellipse(x - 5 - legOffset, y + 4, 6, 3, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.ellipse(x + 5 + legOffset, y + 4, 6, 3, 0, 0, Math.PI * 2);
      ctx.fill();

      // Body/torso (larger)
      ctx.fillStyle = agent.style.primaryColor;
      ctx.beginPath();
      ctx.ellipse(x, y - 30 + bobOffset, 16, 20, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = agent.style.secondaryColor;
      ctx.lineWidth = 2;
      ctx.stroke();

      // Head (larger, more visible)
      ctx.fillStyle = '#FCD9B6'; // Skin tone
      ctx.beginPath();
      ctx.arc(x, y - 55 + bobOffset, 14, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#E5C4A1';
      ctx.lineWidth = 1;
      ctx.stroke();

      // Hair
      ctx.fillStyle = agent.role === 'COORDINATOR' ? '#374151' : agent.role === 'DELEGATOR' ? '#7C3AED' : '#92400E';
      ctx.beginPath();
      ctx.arc(x, y - 60 + bobOffset, 12, Math.PI, Math.PI * 2);
      ctx.fill();

      // Eyes
      ctx.fillStyle = '#1F2937';
      ctx.beginPath();
      ctx.arc(x - 4, y - 55 + bobOffset, 2, 0, Math.PI * 2);
      ctx.arc(x + 4, y - 55 + bobOffset, 2, 0, Math.PI * 2);
      ctx.fill();

      // Smile
      ctx.strokeStyle = '#92400E';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(x, y - 52 + bobOffset, 4, 0.1 * Math.PI, 0.9 * Math.PI);
      ctx.stroke();

      // Role badge on body
      ctx.fillStyle = agent.style.accentColor;
      ctx.beginPath();
      ctx.arc(x, y - 30 + bobOffset, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = agent.style.secondaryColor;
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(agent.role === 'COORDINATOR' ? 'C' : agent.role === 'DELEGATOR' ? 'D' : 'W', x, y - 26 + bobOffset);

      // Activity indicator bubble (using arc instead of roundRect for compatibility)
      if (agent.activity !== 'IDLE' && agent.activity !== 'WALKING') {
        const indicatorY = y - 80 + bobOffset;

        // Bubble background
        ctx.fillStyle = 'rgba(255,255,255,0.95)';
        ctx.beginPath();
        ctx.arc(x, indicatorY, 16, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = agent.style.primaryColor;
        ctx.lineWidth = 2;
        ctx.stroke();

        // Activity icon
        ctx.font = '16px sans-serif';
        ctx.textAlign = 'center';
        const icons: Record<string, string> = {
          WORKING: '💻',
          TYPING: '⌨️',
          THINKING: '💡',
          TALKING: '💬',
          DRINKING_COFFEE: '☕',
          MEETING: '👥',
          PRESENTING: '📊',
        };
        ctx.fillText(icons[agent.activity] || '⏳', x, indicatorY + 6);
      }

      // Name label with background
      ctx.fillStyle = 'rgba(0,0,0,0.7)';
      const nameWidth = ctx.measureText(agent.name).width + 10;
      ctx.fillRect(x - nameWidth / 2, y + 12, nameWidth, 16);
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 10px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(agent.name, x, y + 24);

      // Progress bar if working
      if (agent.activityProgress !== undefined && agent.activityProgress > 0) {
        ctx.fillStyle = 'rgba(0,0,0,0.5)';
        ctx.fillRect(x - 25, y + 32, 50, 6);
        ctx.fillStyle = agent.style.primaryColor;
        ctx.fillRect(x - 25, y + 32, (agent.activityProgress / 100) * 50, 6);
        ctx.strokeStyle = agent.style.secondaryColor;
        ctx.lineWidth = 1;
        ctx.strokeRect(x - 25, y + 32, 50, 6);
      }
    },
    [originX, originY]
  );

  // Apply time-of-day lighting overlay
  const applyLighting = useCallback(
    (ctx: CanvasRenderingContext2D, lighting: DayLighting) => {
      // Ambient overlay
      ctx.fillStyle = lighting.ambientColor;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    },
    [CANVAS_WIDTH, CANVAS_HEIGHT]
  );

  // Main render function
  const render = useCallback(
    (timestamp: number) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Track time for potential future use (smooth animations)
      lastTimeRef.current = timestamp;

      const animFrame = Math.floor(timestamp / 100);

      // Clear canvas
      ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw sky gradient background
      const gradient = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
      gradient.addColorStop(0, state.lighting.skyGradient[0]);
      gradient.addColorStop(1, state.lighting.skyGradient[1]);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      // Draw floor tiles
      for (let row = 0; row < GRID_ROWS; row++) {
        for (let col = 0; col < GRID_COLS; col++) {
          const isCarpet = col >= 12 && col <= 18 && row >= 10 && row <= 14;
          drawTile(
            ctx,
            col,
            row,
            isCarpet ? COLORS.carpet.main : COLORS.floor.main,
            isCarpet ? COLORS.carpet.pattern : COLORS.floor.pattern
          );
        }
      }

      // Collect all renderable objects for depth sorting
      const renderObjects: Array<{
        type: string;
        depth: number;
        render: () => void;
      }> = [];

      // Add furniture to render list
      state.layout.furniture.forEach((furniture) => {
        const depth = calculateDepth(furniture.position.x, furniture.position.y);
        renderObjects.push({
          type: 'furniture',
          depth,
          render: () => {
            switch (furniture.type) {
              case 'DESK_MODERN':
                drawDesk(ctx, furniture.position.x, furniture.position.y);
                break;
              case 'CHAIR_OFFICE':
                drawChair(ctx, furniture.position.x, furniture.position.y);
                break;
              case 'PLANT_LARGE':
                drawPlant(ctx, furniture.position.x, furniture.position.y, true);
                break;
              case 'PLANT_SMALL':
                drawPlant(ctx, furniture.position.x, furniture.position.y, false);
                break;
              case 'COFFEE_MACHINE':
                drawCoffeeMachine(ctx, furniture.position.x, furniture.position.y);
                break;
              case 'SCREEN_LARGE':
              case 'SCREEN_KPI':
                drawKPIScreen(ctx, furniture.position.x, furniture.position.y, state.kpis);
                break;
              case 'SCREEN_ALARM':
                drawAlarmScreen(ctx, furniture.position.x, furniture.position.y, state.kpis, animFrame);
                break;
              case 'SCREEN_RECOMMEND':
                drawRecommendScreen(ctx, furniture.position.x, furniture.position.y);
                break;
            }
          },
        });
      });

      // Add agents to render list
      state.agents.forEach((agent) => {
        const depth = calculateDepth(agent.position.x, agent.position.y, 1);
        renderObjects.push({
          type: 'agent',
          depth,
          render: () => drawAgent(ctx, agent, animFrame),
        });
      });

      // Sort by depth and render (furniture only first)
      const furnitureObjects = renderObjects.filter(obj => obj.type === 'furniture');
      const agentObjects = renderObjects.filter(obj => obj.type === 'agent');

      furnitureObjects.sort((a, b) => a.depth - b.depth);
      furnitureObjects.forEach((obj) => obj.render());

      // Apply lighting overlay BEFORE agents so agents are visible on top
      applyLighting(ctx, state.lighting);

      // NOW draw agents on top of everything (so they're always visible)
      agentObjects.sort((a, b) => a.depth - b.depth);
      agentObjects.forEach((obj) => obj.render());

      // Draw UI overlay (time, status)
      ctx.fillStyle = 'rgba(26, 32, 44, 0.85)';
      ctx.fillRect(20, 20, 200, 90);
      ctx.strokeStyle = '#4A5568';
      ctx.lineWidth = 2;
      ctx.strokeRect(20, 20, 200, 90);

      ctx.fillStyle = '#E2E8F0';
      ctx.font = 'bold 14px JetBrains Mono, monospace';
      ctx.textAlign = 'left';
      ctx.fillText(
        `${state.currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`,
        35,
        42
      );

      ctx.font = '11px Inter, sans-serif';
      ctx.fillStyle = '#A0AEC0';
      ctx.fillText(`Speed: ${state.speed}x`, 35, 58);
      ctx.fillText(
        `Status: ${state.isRunning ? (state.isPaused ? 'PAUSED' : 'RUNNING') : 'STOPPED'}`,
        35,
        74
      );

      // Show agent count
      ctx.fillStyle = state.agents.length > 0 ? '#48BB78' : '#F56565';
      ctx.fillText(`Agents Active: ${state.agents.length}`, 35, 100);

      // Pulsing indicator for running state
      if (state.isRunning && !state.isPaused) {
        const pulse = (Math.sin(timestamp / 500) + 1) / 2;
        ctx.fillStyle = `rgba(72, 187, 120, ${0.5 + pulse * 0.5})`;
        ctx.beginPath();
        ctx.arc(190, 35, 6, 0, Math.PI * 2);
        ctx.fill();
      }

      // NOTE: Animation loop is handled by useEffect, not here
    },
    [
      state,
      CANVAS_WIDTH,
      CANVAS_HEIGHT,
      GRID_ROWS,
      GRID_COLS,
      drawTile,
      drawDesk,
      drawChair,
      drawPlant,
      drawCoffeeMachine,
      drawKPIScreen,
      drawAlarmScreen,
      drawRecommendScreen,
      drawAgent,
      applyLighting,
    ]
  );

  // Start/stop animation loop - ALWAYS run to show agents
  useEffect(() => {
    let animating = true;
    let slowModeTimeout: ReturnType<typeof setTimeout> | null = null;

    const animate = (timestamp: number) => {
      if (!animating) return;

      // Always render
      render(timestamp);

      // Continue animation - fast when running, slow when not
      if (state.isRunning && !state.isPaused) {
        // Fast animation for active simulation
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        // Slow update when paused/stopped (every 500ms) to show agents without burning CPU
        slowModeTimeout = setTimeout(() => {
          if (animating) {
            animationFrameRef.current = requestAnimationFrame(animate);
          }
        }, 500);
      }
    };

    // Initial render immediately
    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      animating = false;
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (slowModeTimeout) {
        clearTimeout(slowModeTimeout);
      }
    };
  }, [state.isRunning, state.isPaused, render]);

  // Force a re-render when agents change (to ensure they're visible)
  useEffect(() => {
    if (canvasRef.current) {
      // Trigger immediate render when agents update
      render(performance.now());
    }
  }, [state.agents, render]);

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className="rounded-lg shadow-2xl border border-slate-700"
        style={{
          maxWidth: '100%',
          height: 'auto',
        }}
      />

      {/* Alarm indicators overlay */}
      {state.activeAlarms.length > 0 && (
        <div className="absolute top-4 right-4 bg-red-500/90 text-white px-4 py-2 rounded-lg animate-pulse">
          <span className="font-bold">{state.activeAlarms.length} Active Alarm(s)</span>
        </div>
      )}
    </div>
  );
};
